import type { Registry as Services } from '@ember/service';
import { tKey } from 'ember-intl';
import { MIN_LOS_NO_RESTRICTION_VALUE } from 'lumen/utils/constants/pricing-assistant-constants';

const AMOUNT_MORE_KEY = tKey('general.amount_more');

const MAX_NAMED_ITEMS = 2;

export interface MinLosFields {
  hasOverride: boolean;
  isLoading: boolean;
  value: number | null;
}

export interface RateGroupMinLos extends MinLosFields {
  rateGroupId: string;
}

export interface MinLosRow<T extends MinLosFields> extends MinLosFields {
  items: T[];
}

/**
 * Collapses items (rate groups, rooms) that would render an identical MinLOS row
 * into one row. Each row keeps its items, so the caller labels them its own way.
 *
 * `hasOverride` is part of the merge key because it changes how the value is drawn:
 * two items both showing "MinLOS 2" must stay apart when only one shows the
 * override indicator — the icon asserts "automation paused" for every item in the
 * row, so OR-ing it like `isLoading` would put a false claim on items without an
 * override.
 *
 * `isLoading` is not part of the key, so a merged row can cover both a settled item
 * and one mid-push. Such a row loads when any of its items does, matching the single
 * row it replaces: a skeleton never asserts a value that is about to change.
 */
export function groupByMinLos<T extends MinLosFields>(
  entries: T[],
): MinLosRow<T>[] {
  const rows: MinLosRow<T>[] = [];

  for (const entry of entries) {
    const existingRow = rows.find(
      (row) =>
        row.value === entry.value && row.hasOverride === entry.hasOverride,
    );

    if (existingRow) {
      existingRow.items.push(entry);
      existingRow.isLoading = existingRow.isLoading || entry.isLoading;
      continue;
    }

    rows.push({
      hasOverride: entry.hasOverride,
      isLoading: entry.isLoading,
      items: [entry],
      value: entry.value,
    });
  }

  return rows;
}

/**
 * Labels a merged row, e.g. "Group 1, Group 2, 2 more...".
 *
 * Names are shown verbatim rather than translated: the hotel wrote them in CEMA.
 */
export function formatNamesLabel(
  names: string[],
  intl: Services['intl'],
): string {
  const labelParts = names.slice(0, MAX_NAMED_ITEMS);
  const remainingCount = names.length - labelParts.length;

  if (remainingCount > 0) {
    labelParts.push(intl.t(AMOUNT_MORE_KEY, { count: remainingCount }));
  }

  return labelParts.join(', ');
}

/**
 * Whether a MinLOS value is a restriction, e.g. a rate group's MinLOS column value
 * (MinLosRestrictionState.minLosColumnValue). "No MinLOS restriction" and no value (e.g.
 * "No recommendation") both count as not restricted.
 */
export function isMinLosRestricted(value: number | null | undefined): boolean {
  return value != null && value !== MIN_LOS_NO_RESTRICTION_VALUE;
}
